"use client";

import { useMemo, useState } from "react";
import { HeatmapLayer } from "@/components/map/heatmap-layer";
import { HoverCard } from "@/components/map/hover-card";
import { Legend } from "@/components/map/legend";
import { PulseShaderLayer } from "@/components/map/pulse-layer";
import { TimeSlider } from "@/components/map/time-slider";
import type { PressurePoint, Region } from "@/types/geodata";

function project(lat: number, lng: number) {
  return {
    left: `${((lng + 180) / 360) * 100}%`,
    top: `${((90 - lat) / 180) * 100}%`,
  };
}

export function MapCanvasBasic({
  regions,
  pressurePoints,
}: {
  regions: Region[];
  pressurePoints: PressurePoint[];
}) {
  const [activeRegionId, setActiveRegionId] = useState(regions[0]?.id);
  const activeRegion = useMemo(
    () => regions.find((region) => region.id === activeRegionId),
    [activeRegionId, regions],
  );

  return (
    <div className="map-grid relative h-full min-h-[72vh] overflow-hidden bg-[#101820] lg:min-h-screen">
      <div className="absolute inset-0 opacity-70 [background:radial-gradient(circle_at_50%_45%,rgba(31,116,107,.34),transparent_34%),linear-gradient(140deg,rgba(42,50,56,.9),rgba(10,15,20,.95))]" />
      <HeatmapLayer points={pressurePoints} />
      <PulseShaderLayer points={pressurePoints} />
      {regions.map((region) => (
        <button
          key={region.id}
          type="button"
          className="absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-md border border-white/20 bg-black/55 px-2 py-1 text-left text-xs shadow-lg backdrop-blur transition hover:border-amber-300"
          style={project(region.coordinates.lat, region.coordinates.lng)}
          onMouseEnter={() => setActiveRegionId(region.id)}
          onFocus={() => setActiveRegionId(region.id)}
        >
          <span className="block font-medium">{region.name}</span>
          <span className="text-red-200">{region.pressureIndex} pressure</span>
        </button>
      ))}
      <HoverCard region={activeRegion} />
      <Legend />
      <TimeSlider />
    </div>
  );
}
